<template>
  <div id="request-details">
    <Breadcrumbs
      :breadcrumbs="breadcrumbs">
    </Breadcrumbs>

    <div v-if="isLoading" class="d-flex flex-column justify-content-center align-items-center py-5">
      <span class="mb-3">{{$t('evocall.requests.loading')}}...</span>
      <LoadingAnimation />
    </div>

    <div v-else-if="request !== null && appointment !== null">

      <div class="row" v-if="request && false">
        <div class="col-12 col-md-8">
          <video-area :request="request"></video-area>
        </div>
        <div class="col-12 col-md-4">
          <chat-area :request="request"></chat-area>
        </div>
      </div>

      <section class="appointment-details mb-5" v-if="appointment && person">
        <PersonShortInfo
          v-if="person"
          class="mb-5"
          :person="person"
          :service-slug="serviceSlug"
        />

        <AppointmentShortInfo
          v-if="parentAppointment"
          class="mb-5"
          :appointment="parentAppointment"
          :service-slug="serviceSlug"
        />

        <va-button
          id="btn-see-appointment-details"
          class="btn-gray btn-big"
          @click="onClickShowAppointmentDetails"
        >
          {{$t('evocall.requests.see_appointment_details')}}
        </va-button>

        <AppointmentAnamnesisModal
          v-if="showAppointmentDetails"
          :appointment="parentAppointment"
          @modalClosed="closeAppointmentDetails"
          :serviceSlug="serviceSlug"
          :request="request"
          :loadedPerson="person"
        >
        </AppointmentAnamnesisModal>
      </section>

      <section class="questionnaire-section">

        <h6 class="is-bold">
          {{$t('clinical.people.anamnesis.clinical_questionnaire')}}
        </h6>

        <OtoscopyForm
          ref="otoscopyForm"
          class="ml-3"
          v-model="form.data.otoscopy"
          :formErrors="form.anamnesisErrors.nestedErrors('otoscopy.')"
          @clearErrors="field => {form.anamnesisErrors.clear(`otoscopy.${field}`); form.appointmentErrors.clear('otoscopy')}"
          :collapsable="true"
          :editing="form.data.with_otoscopy !== null && form.data.with_otoscopy"
        >
          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(form.data.otoscopy)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>

            <!-- With Otoscopy -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_otoscopy')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="form.data.with_otoscopy"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="form.appointmentErrors.clear('with_otoscopy'); form.appointmentErrors.clear('otoscopy')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('with_otoscopy')">
                <small>
                  {{$t(form.appointmentErrors.get('with_otoscopy'))}}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('otoscopy')">
                <small>
                  {{$t(form.appointmentErrors.get('otoscopy'))}}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </OtoscopyForm>

        <ImpedanceForm
          ref="impedanceForm"
          class="ml-3"
          v-model="form.data.impedance"
          :formErrors="form.anamnesisErrors.nestedErrors('impedance.')"
          @clearErrors="field => {form.anamnesisErrors.clear(`impedance.${field}`); form.appointmentErrors.clear('impedance')}"
          :collapsable="true"
          :editing="form.data.with_impedance !== null && form.data.with_impedance"
        >
          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(form.data.impedance)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>

            <!-- With Impedance -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_impedance')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="form.data.with_impedance"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="form.appointmentErrors.clear('with_impedance'); form.appointmentErrors.clear('impedance')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('with_impedance')">
                <small>
                  {{$t(form.appointmentErrors.get('with_impedance'))}}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('impedance')">
                <small>
                  {{$t(form.appointmentErrors.get('impedance'))}}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </ImpedanceForm>

        <TonalAudiometryForm
          ref="tonalAudiometryForm"
          class="ml-3"
          v-model="form.data.tonal_audiometry"
          :formErrors="form.anamnesisErrors.nestedErrors('tonal_audiometry.')"
          @clearErrors="field => {form.anamnesisErrors.clear(`tonal_audiometry.${field}`); form.appointmentErrors.clear('tonal_audiometry')}"
          :editing="form.data.with_tonal_audiometry !== null && form.data.with_tonal_audiometry"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(form.data.tonal_audiometry)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>

            <!-- With Tonal Audiometry -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_tonal_audiometry')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="form.data.with_tonal_audiometry"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="form.appointmentErrors.clear('with_tonal_audiometry'); form.appointmentErrors.clear('tonal_audiometry')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('with_tonal_audiometry')">
                <small>
                  {{$t(form.appointmentErrors.get('with_tonal_audiometry'))}}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('tonal_audiometry')">
                <small>
                  {{$t(form.appointmentErrors.get('tonal_audiometry'))}}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </TonalAudiometryForm>

        <VocalAudiometryForm
          ref="vocalAudiometryForm"
          class="ml-3"
          v-model="form.data.vocal_audiometry"
          :formErrors="form.anamnesisErrors.nestedErrors('vocal_audiometry.')"
          @clearErrors="field => {form.anamnesisErrors.clear(`vocal_audiometry.${field}`); form.appointmentErrors.clear('vocal_audiometry')}"
          :editing="form.data.with_vocal_audiometry !== null && form.data.with_vocal_audiometry"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(form.data.vocal_audiometry)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>

            <!-- With Vocal Audiometry -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_vocal_audiometry')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="form.data.with_vocal_audiometry"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="form.appointmentErrors.clear('with_vocal_audiometry'); form.appointmentErrors.clear('vocal_audiometry')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('with_vocal_audiometry')">
                <small>
                  {{$t(form.appointmentErrors.get('with_vocal_audiometry'))}}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="form.appointmentErrors.has('vocal_audiometry')">
                <small>
                  {{$t(form.appointmentErrors.get('vocal_audiometry'))}}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </VocalAudiometryForm>

        <ObservationsForm
          class="ml-3"
          v-model="form.data.anamnesis_obs"
          :formErrors="form.appointmentErrors.rootErrors()"
          :collapsable="false"
        />

      </section>

      <div class="actions-wrapper d-flex flex-column flex-sm-row" :class="activeRequestHasUnsavedPersonChanges || activeRequestHasUnsavedAppointmentChanges ? 'justify-content-between' : 'justify-content-end'" >
        <va-button
          v-if="activeRequestHasUnsavedAppointmentChanges || activeRequestHasUnsavedPersonChanges"
          class="btn-cancel order-2 order-sm-1"
          @click="onClickEndWithoutSaving"
          :disabled="isSubmitingAppointmentForm || inCall">
          {{ getNoSaveButtonText }}
        </va-button>

        <div class="d-flex justify-content-end align-items-center order-1 order-sm-2">

          <va-button
            id="btn-save"
            @click="onClickSave"
            :disabled="isSubmitingAppointmentForm || !activeRequestHasUnsavedAppointmentChanges && !activeRequestHasUnsavedPersonChanges">
            {{ $t('clinical.people.anamnesis.save') }}
          </va-button>

          <va-button
            id="btn-save-and-end"
            @click="onClickSaveAndEnd"
            :disabled="isSubmitingAppointmentForm || inCall">
            {{ getSaveButtonText }}
          </va-button>
        </div>
      </div>
    </div>

    <div v-else class="d-flex align-items-center flex-column py-5">
      <span class="mb-3">{{$t('evocall.requests.loading_failed')}}</span>
    </div>

    <ConfirmationModal
      v-if="showGenericConfirmationModal"
      :title="confirmationModalOptions.title"
      :body="confirmationModalOptions.message"
      @modal-confirmed="modalConfirmed"
      @modal-canceled="resetConfirmationModals"
    />

    <ConfirmationModal
      v-if="showLeaveConfirmationModal"
      :ok-text="$t('clinical.people.anamnesis.common_options.yes')"
      :cancel-text="$t('clinical.people.anamnesis.common_options.no')"
      :title="activeRequestHasUnsavedPersonChanges || activeRequestHasUnsavedAppointmentChanges ? $t('clinical.people.anamnesis.modal_has_unsaved_changes_title') : $t('clinical.people.anamnesis.modal_conclude_title')"
      :body="activeRequestHasUnsavedPersonChanges || activeRequestHasUnsavedAppointmentChanges ? $t('clinical.people.anamnesis.modal_has_unsaved_changes') : $t('clinical.people.anamnesis.modal_conclude')"
      @modal-confirmed="onConfirmLeave"
      @modal-canceled="onCancelLeave"
    />

  </div>
</template>
<script>
import { mapState } from 'vuex'
import EventBus from '@/utils/EventBus'
import Errors from '@/utils/StandardErrors'
import { HollowDotsSpinner } from 'epic-spinners'
import {MyService, RequestService} from '@/api/EvocallService'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import LoadingAnimation from '@/components/common/LoadingAnimation'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { AppointmentService, PersonService } from '@/api/AuthClinicalService'
import PersonInfo from '@/components/clinical/people/PersonDetails/PersonInfo'
import OtoscopyForm from '@/components/clinical/people/Anamnesis/OtoscopyForm'
import ObservationsForm from '@/components/clinical/people/Anamnesis/ObservationsForm'
import { booleanOptions } from '@/components/clinical/people/Anamnesis/AnamnesisOptions'
import PersonShortInfo from '@/components/clinical/people/PersonDetails/PersonShortInfo'
import AppointmentShortInfo from '@/components/clinical/appointments/AppointmentShortInfo'
import TonalAudiometryForm from '@/components/clinical/people/Anamnesis/TonalAudiometryForm'
import VocalAudiometryForm from '@/components/clinical/people/Anamnesis/VocalAudiometryForm'
import AppointmentAnamnesisModal from '@/components/clinical/appointments/AppointmentAnamnesisModal'
import ImpedanceForm from "@/components/clinical/people/Anamnesis/ImpedanceForm";

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  components: {
    ImpedanceForm,
    PersonInfo,
    Breadcrumbs,
    OtoscopyForm,
    PersonShortInfo,
    ObservationsForm,
    LoadingAnimation,
    ConfirmationModal,
    HollowDotsSpinner,
    TonalAudiometryForm,
    VocalAudiometryForm,
    AppointmentShortInfo,
    AppointmentAnamnesisModal
  },
  data () {
    return {
      request: null,
      person: null,
      appointment: null,
      parentAppointment: null,
      form: {
        data: {
          with_otoscopy: false,
          otoscopy: null,
          with_impedance: false,
          impedance: null,
          with_tonal_audiometry: false,
          tonal_audiometry: null,
          with_vocal_audiometry: false,
          vocal_audiometry: null,
          anamnesis_obs: null
        },
        appointmentErrors: new Errors(),
        anamnesisErrors: new Errors()
      },
      confirmationModalOptions: {
        action: null,
        message: null,
        title: null
      },
      isLoading: true,
      isSubmitingAppointmentForm: false,
      showAppointmentDetails: false,
      showGenericConfirmationModal: false,
      showLeaveConfirmationModal: false,
      hasLoadedAppointmentForm: false,
      onConfirmLeaveCallback: null,
      booleanOptions,
      needsRefreshToast: null,
      clickedEndOrSaveAndEnd: false,
      forcedPageExit: false
    }
  },
  mounted () {
    //Temporarily disabled due to unwanted behaviours
    //window.addEventListener('focus', this.windowFocused)

    EventBus.$on('REQUEST_REQUEUED', (payload) => {
      this.forcedPageExit = true
      this.redirectToQueue()
    })
  },
  methods: {
    windowFocused: async function () {
      if (this.authClinicalService) {
        const activeRequest = await MyService.getActiveRequest({ serviceId: this.authClinicalService.id })
        if (this.request === null || activeRequest === null || activeRequest.id !== this.request.id) {
          EventBus.$emit('REQUEST_REQUEUED', { request: this.request })
          this.$store.commit(`${this.waitingRoomModuleKey}/RESET_ACTIVE_REQUEST`)
          this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, { hasUnsavedChanges: false })
          this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, { hasUnsavedChanges: false })
        }
      }

    },
    onClickShowAppointmentDetails: function () {
      this.showAppointmentDetails = true
    },
    closeAppointmentDetails: function () {
      this.showAppointmentDetails = false
    },
    onClickEndWithoutSaving: function () {
      this.clickedEndOrSaveAndEnd = true
      this.confirmationModalOptions = {
        title: this.$t('evocall.requests.actions.confirm_end_without_saving'),
        message: this.$t('evocall.requests.actions.confirm_end_no_save'),
        action: 'conclude-without-saving',
      }
      this.showGenericConfirmationModal = true
    },
    onClickSaveAndEnd: function () {
      this.clickedEndOrSaveAndEnd = true
      this.confirmationModalOptions = {
        title: this.$t('evocall.requests.actions.confirm_save_and_end'),
        message: this.$t('evocall.requests.actions.confirm_end'),
        action: !this.activeRequestHasUnsavedPersonChanges && !this.activeRequestHasUnsavedAppointmentChanges ? 'conclude-without-saving' : 'conclude-and-save',
      }
      this.showGenericConfirmationModal = true
    },
    onClickSave: function () {
      this.submitAppointmentForm()
    },
    modalConfirmed: function () {
      switch (this.confirmationModalOptions.action) {
      case 'conclude-without-saving':
        this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, { hasUnsavedChanges: false })
        this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, { hasUnsavedChanges: false })
        this.concludeRequest()
        break
      case 'conclude-and-save':
        this.submitAppointmentForm(true)
        break
      default:
      }
      this.resetConfirmationModals()
    },
    onConfirmLeave: async function () {
      this.showLeaveConfirmationModal = false
      this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, { hasUnsavedChanges: false })
      this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, { hasUnsavedChanges: false })
      //this.concludeRequest()
      if (this.onConfirmLeaveCallback !== null) {
        if (typeof this.onConfirmLeaveCallback === 'string') {
          this[this.onConfirmLeaveCallback]()
        } else {
          this.onConfirmLeaveCallback()
        }
      }
    },
    onCancelLeave: function () {
      this.clickedEndOrSaveAndEnd = false
      this.showLeaveConfirmationModal = false
      this.onConfirmLeaveCallback = null
    },
    resetConfirmationModals: function () {
      this.showGenericConfirmationModal = false
      this.confirmationModalOptions = {
        message: null,
        action: null
      }
    },
    concludeRequest: function () {
      RequestService.conclude(this.request.id)
        .then(() => {
          this.showToastInCall(this.$t('evocall.request.actions.ended'), this.$store.state.successToastOptions)
          this.redirectToQueue()
        }).catch(errors => {
          console.log(errors)
          if (errors.response && errors.response.status) {
            switch (errors.response.status) {
            case 403:
              this.showToastInCall(this.$t('evocall.requests.errors.missing_permissions'), this.$store.state.errorToastOptions)
              break
            case 404:
              this.showToastInCall(this.$t('evocall.requests.errors.not_found'), this.$store.state.errorToastOptions)
              break
            default:
              this.showToastInCall(this.$t('clinical.appointments.something_went_wrong'), this.$store.state.errorToastOptions)
              break
            }
          }
        })
    },
    redirectToQueue: function () {
      if (this.showGenericConfirmationModal) {
        this.showGenericConfirmationModal = false
      }
      this.$nextTick(() => this.$router.push({ name: `${this.serviceSlug}evocall/requests` }))
    },
    loadRequest: function (requestId) {
      RequestService.get(requestId)
        .then(request => {
          this.request = request
          return this.loadAppointment(request.appointment_id)
        })
        .then(appointment => {
          return this.loadPerson(appointment.person.id)
        })
        .then(person => {
          this.fillAppointmentForm()
          this.isLoading = false
        })
        .catch(errors => {
          console.log(errors)
          this.isLoading = false
          // TODO: redirect user out of the page (but we first need a way for him to get back)
          // this.forcedPageExit = true
          // this.redirectToQueue()
          this.showToast(this.$t('evocall.requests.errors.load_failed'), this.$store.state.errorToastOptions)
        })
    },
    loadAppointment: function (appointmentId) {
      if (this.authClinicalService) {
        return new Promise((resolve, reject) => {
          AppointmentService.get(this.authClinicalService.url, appointmentId)
            .then(appointment => {
              this.appointment = appointment
              this.loadParentAppointment(appointment.parent_id)
                .then(() => resolve(appointment))
                .catch(errors => reject(errors))
            })
            .catch(errors => reject(errors))
        })
      }
    },
    loadParentAppointment: function (appointmentId) {
      if (this.authClinicalService) {
        return new Promise((resolve, reject) => {
          AppointmentService.get(this.authClinicalService.url, appointmentId)
            .then(appointment => {
              this.parentAppointment = appointment
              resolve(appointment)
            })
            .catch(error => {
              reject(error)
            })
        })
      }
    },
    loadPerson: function (personId) {
      if (this.authClinicalService) {
        return new Promise((resolve, reject) => {
          PersonService.get(this.authClinicalService.url, personId)
            .then(person => {
              this.person = person
              resolve(person)
            })
            .catch(error => reject(error))
        })
      }
    },
    submitAppointmentForm: function (shouldConclude = false) {
      if (!this.isSubmitingAppointmentForm) {
        this.isSubmitingAppointmentForm = true
        AppointmentService.update(this.authClinicalService.url, this.appointment.id, this.getAppointmentFormData())
          .then(response => {
            let appointment = response.data.data
            this.showToastInCall(this.$t('clinical.appointments.appointment_updated_with_anamnesis'), this.$store.state.successToastOptions)
            if (response.data.error) {
              setTimeout(() => {
                this.showToastInCall(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
              }, this.$store.state.successToastOptions.duration)
            }
            this.hasLoadedAppointmentForm = false
            this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, { hasUnsavedChanges: false })
            this.$store.dispatch(`${this.waitingRoomModuleKey}/ACTIVE_REQUEST_CHANGES_SAVED`, { field: 'appointment' })
            this.appointment = appointment
            this.fillAppointmentForm(true)
            if (shouldConclude) {
              this.concludeRequest()
            }
          })
          .catch(errors => {
            console.log(errors)
            if (errors.response && errors.response.status) {
              switch (errors.response.status) {
              case 403:
                this.showToastInCall(this.$t('clinical.appointments.missing_permissions_for_update'), this.$store.state.errorToastOptions)
                break
              case 404:
                this.showToastInCall(this.$t('clinical.appointments.errors.not_found'), this.$store.state.errorToastOptions)
                break
              case 422:
                this.form.appointmentErrors.record(errors.response.data.errors, 'clinical.appointments.errors.')
                this.form.anamnesisErrors.record(errors.response.data.errors, 'clinical.people.anamnesis.errors.')
                this.showToastInCall(this.$t('clinical.appointments.appointment_form_contains_errors'), this.$store.state.errorToastOptions)
                break
              default:
                this.showToastInCall(this.$t('clinical.appointments.something_went_wrong'), this.$store.state.errorToastOptions)
                break
              }
            }
          })
          .finally(() => {
            this.isSubmitingAppointmentForm = false
          })
      }
    },
    getAppointmentFormData: function () {
      let formData = JSON.parse(JSON.stringify(this.form.data))

      if (this.$refs.otoscopyForm.getHasUnsavedChanges()) {
        if (this.form.data.with_otoscopy) {
          formData.otoscopy = this.getAnamnesisFormData(this.form.data.otoscopy)
        } else {
          delete formData.otoscopy
        }
      } else {
        delete formData.with_otoscopy
        delete formData.otoscopy
      }

      if (this.$refs.impedanceForm.getHasUnsavedChanges()) {
        if (this.form.data.with_impedance) {
          formData.impedance = this.getAnamnesisFormData(this.form.data.impedance)
        } else {
          delete formData.impedance
        }
      } else {
        delete formData.with_impedance
        delete formData.impedance
      }

      if (this.$refs.tonalAudiometryForm.getHasUnsavedChanges()) {
        if (this.form.data.with_tonal_audiometry) {
          formData.tonal_audiometry = this.getAnamnesisFormData(this.form.data.tonal_audiometry)
        } else {
          delete formData.tonal_audiometry
        }
      } else {
        delete formData.with_tonal_audiometry
        delete formData.tonal_audiometry
      }

      if (this.$refs.vocalAudiometryForm.getHasUnsavedChanges()) {
        if (this.form.data.with_vocal_audiometry) {
          formData.vocal_audiometry = this.getAnamnesisFormData(this.form.data.vocal_audiometry)
        } else {
          delete formData.vocal_audiometry
        }
      } else {
        delete formData.with_vocal_audiometry
        delete formData.vocal_audiometry
      }

      return formData
    },
    getAnamnesisFormData: function (form) {
      if (form !== null) {
        let formData = this.sanitizeAnamnesisModel(form)
        let id = form.hasOwnProperty('id') ? form.id : undefined
        delete formData.id

        formData = Object.values(formData).some(p => (Array.isArray(p) && p.length > 0) || (!Array.isArray(p) && p !== null && p !== undefined && (typeof p === 'string' ? p.trim() !== '' : true))) ? formData : undefined

        if (formData !== undefined && id !== undefined && id !== null) {
          formData.id = id
        }

        return formData
      } else {
        return undefined
      }
    },
    sanitizeAnamnesisModel: function (model) {
      let data = JSON.parse(JSON.stringify(model))
      delete data.appointment_id
      delete data.person_id
      delete data.created_by
      delete data.created_at
      delete data.updated_by
      delete data.updated_at
      return data
    },
    onRequestUpdated: function (request) {
      this.request = request
    },
    getLatestAnamnesisModel: function (models, defaultValue = null) {
      if (Array.isArray(models) && models.length > 0) {
        models.sort((a, b) => this.moment(b.updated_at).diff(a.updated_at))
        return models[0]
      }
      return defaultValue
    },
    fillAppointmentForm: function (resetUnsavedChanges = false) {
      this.form.data.with_otoscopy = this.appointment.with_otoscopy || false
      this.form.data.with_impedance = this.appointment.with_impedance || false
      this.form.data.with_tonal_audiometry = this.appointment.with_tonal_audiometry || false
      this.form.data.with_vocal_audiometry = this.appointment.with_vocal_audiometry || false
      this.form.data.anamnesis_obs = this.appointment.anamnesis_obs

      this.setAppointmentFormField('otoscopies', 'otoscopy')
      this.setAppointmentFormField('impedances', 'impedance')
      this.setAppointmentFormField('tonal_audiometries', 'tonal_audiometry')
      this.setAppointmentFormField('vocal_audiometries', 'vocal_audiometry')

      if (resetUnsavedChanges) {
        this.resetUnsavedChanges()
      }

      this.$nextTick(() => {
        this.hasLoadedAppointmentForm = true
      })
    },
    setAppointmentFormField: function (modelField, formField, useAppointmentData = true) {
      let models = []

      if (useAppointmentData) {
        models = models.concat(this.appointment[modelField])
      }

      if (Array.isArray(this.person[modelField]) && this.person[modelField].length > 0) {
        const appointmentIds = [this.appointment.id, this.appointment.parent_id].filter(v => v)
        models = models.concat(
          this.person[modelField].filter(model => !appointmentIds.includes(model.appointment_id))
        )
      }

      let latestModel = this.getLatestAnamnesisModel(models, null)

      if (latestModel !== null) {
        latestModel = JSON.parse(JSON.stringify(latestModel))

        if (latestModel.appointment_id !== this.appointment.id) {
          delete latestModel.id
        }
      }

      this.form.data[formField] = latestModel
    },
    resetUnsavedChanges: function () {
      this.getAnamnesisFormsFromRefs()
        .forEach(form => {
          form.setHasUnsavedChanges(false)
        })
    },
    getAnamnesisFormsFromRefs: function () {
      return Object.keys(this.$refs).filter(key => key.toLowerCase().includes('form')).map(key => this.$refs[key])
    },
    isOutdatedAnamnesisModel: function (model) {
      return model !== null && this.person.last_appointment_date !== null && this.moment(model.updated_at).isBefore(this.moment(this.person.last_appointment_date))
    },
    showToastInCall: function (message, options) {
      let customOptions = { ...options }
      if (this.inCall) {
        customOptions.position = 'top-left'
      }
      return this.showToast(message, customOptions)
    },
    showNeedsRefreshToast: function () {
      let options = {
        ...this.$store.state.successToastOptions,
        duration: null,
        position: 'top-left',
        action: {
          text: this.$t('clinical.appointment.refresh_appointment_data_button'),
          onClick: (e, toastObject) => {
            this.loadParentAppointment(this.appointment.parent_id)
            toastObject.goAway(250)
            this.needsRefreshToast = null
          }
        }
      }
      this.needsRefreshToast = this.showToastInCall(this.$t('clinical.appointment.needs_refresh'), options)
    }
  },
  computed: {
    authClinicalService: function () {
      return this.request ? this.$store.state.services.find(s => s.id === this.request.service_id) : null
    },
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.serviceSlug}`
    },
    ...mapState({
      activeAppointment (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeAppointment`] || null
      },
      activeRequest (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequest`] || null
      },
      inCall (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/inCall`] || null
      },
      activeRequestHasUnsavedPersonChanges (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequestHasUnsavedPersonChanges`] || false
      },
      activeRequestHasUnsavedAppointmentChanges (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequestHasUnsavedAppointmentChanges`] || false
      },
    }),
    getSaveButtonText: function () {
      if (!this.activeRequestHasUnsavedPersonChanges && !this.activeRequestHasUnsavedAppointmentChanges) {
        return this.$t('evocall.requests.actions.exit')
      } else {
        return this.$t('evocall.requests.actions.save_and_exit')
      }
    },
    getNoSaveButtonText: function () {
      return this.$t('evocall.requests.actions.exit_no_save')
    },
    breadcrumbs: function () {
      let breadcrumbs = [
        { title: this.$t('menu.evocall'), disabled: true },
        { title: this.$t('menu.evocall/requests'), meta: { type: 'function', value: () => { this.$router.push({ name: `${this.authClinicalService.slug}evocall/requests` }) } } }
      ]
      if (this.appointment) {
        breadcrumbs.push({ title: `${this.appointment.person.name}`, active: true })
      }
      return breadcrumbs
    }
  },
  sockets: {
    'active-request-appointment-updated': function (arg1, arg2) {
      if (this.needsRefreshToast === null) {
        this.showNeedsRefreshToast()
      }
    }
  },
  watch: {
    'form.data.with_otoscopy': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('otoscopies', 'otoscopy', false)
      }

      if (this.$refs.hasOwnProperty('otoscopyForm') && newVal !== oldVal) {
        this.$refs.otoscopyForm.setHasUnsavedChanges(true)
      }
    },
    'form.data.with_impedance': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('impedances', 'impedance', false)
      }

      if (this.$refs.hasOwnProperty('impedanceForm') && newVal !== oldVal) {
        this.$refs.impedanceForm.setHasUnsavedChanges(true)
      }
    },
    'form.data.with_tonal_audiometry': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('tonal_audiometries', 'tonal_audiometry', false)
      }

      if (this.$refs.hasOwnProperty('tonalAudiometryForm') && newVal !== oldVal) {
        this.$refs.tonalAudiometryForm.setHasUnsavedChanges(true)
      }
    },
    'form.data.with_vocal_audiometry': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('vocal_audiometries', 'vocal_audiometry', false)
      }

      if (this.$refs.hasOwnProperty('vocalAudiometryForm') && newVal !== oldVal) {
        this.$refs.vocalAudiometryForm.setHasUnsavedChanges(true)
      }
    },
    'form.data': {
      handler: function (value) {
        if (this.hasLoadedAppointmentForm && !this.activeRequestHasUnsavedAppointmentChanges) {
          this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, { hasUnsavedChanges: true })
        }
      },
      deep: true
    },
  },
  beforeDestroy: function () {
    if (this.needsRefreshToast != null) {
      this.needsRefreshToast.goAway(100)
    }
  },
  beforeRouteEnter (to, from, next) {
    if (to.params.id !== undefined) {
      next(vm => {
        vm.loadRequest(parseInt(to.params.id))
        vm.from = from
      })
    } else {
      next(from)
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params.id !== undefined) {
      this.loadRequest(parseInt(to.params.id))
    } else {
      next(from)
    }
  },
  beforeRouteLeave: function (to, from, next) {
    if (!this.forcedPageExit && ((this.activeRequestHasUnsavedPersonChanges || this.activeRequestHasUnsavedAppointmentChanges) || (!this.inCall && !this.clickedEndOrSaveAndEnd))) {
      this.showLeaveConfirmationModal = true
      this.$store.commit(`${this.waitingRoomModuleKey}/RESET_CURRENT_ACTIVE_APPOINTMENT`)
      this.onConfirmLeaveCallback = next
    } else {
      next()
    }
  }
}
</script>

<style lang="scss">

#request-details {

  .questionnaire-section {
    margin-bottom: 3rem;
  }

  .actions-wrapper {
    .va-button {
      min-width: 8rem;
    }
  }
}

</style>
<style lang="scss" scoped>
// .video {
//   position: relative;
//   min-height: 200px;
//   // height: 100%;
//   &__partner {
//     height: 100%;
//     width: 100%;
//   }
//   &__myself {
//     bottom: 6px;
//     position: absolute;
//     float: right;
//     right: 6px;
//     width: 150px;
//     height: 100px;
//     z-index: 2;
//   }
//   &__spinner {
//     width: 100% !important;
//     height: 100% !important;
//   }
// }

.item {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(58, 58, 58);
  color: whitesmoke;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 1rem;

  &.item--audio {
    background-color: #7F62F6;
  }
  &.item--video {
    background-color: #28A5FC;
  }
  &.item--network {
    background-color: #1ED78D;
  }

  &:hover {
    cursor: pointer;
  }

  &.item__action--turn-off {
    background-color: rgb(236, 69, 69);
  }
  &.item__action--call {
    background-color: #29c4a9;
  }
}

.chat__header {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
}

</style>
<style lang="scss">

@mixin for-desktop {
  @media (min-width: 599px) {
    @content;
  }
}

// .sc-chat-window.opened {
//   width: initial;
//   height: 100% !important;
// }

.no-fixed {
  height: 100%;
  & .sc-chat-window {
    width: initial;
    position: initial;
    z-index: 11;
    height: calc(100% - 1.25rem);
  }
}

.sc-message--text-content {
  color: #fff;
}

.sc-message--avatar {
  display: none !important;
}

.video__chat {
  // height: 550px;
  // height: 750px;

  &.video__chat__preview {
    display: flex;
    flex-direction: column;
    // justify-content: center;
    align-items: center;

    & .video__container {
      display: flex;
      flex-direction: column;
      // position: relative;
      width: 100%;
      // height: 65%;
      //top: 0;
      //left: 0;
      border-radius: 10px;
      overflow: hidden;
      @include for-desktop {
        width: 75%;
      }
      & .video__myself {
        // position: relative;
        // top: 0;
        // left: 0;
        // width: 100%;
        // height: 100%;
        // border-radius: 15px;
        height: 250px;
        max-height: 400px;
        @include for-desktop {
          height: auto;
          // max-height: none;
        }
      }

      & .video__controls {
        // position: absolute;
        // top: 0px;
        // right: -10px;
        // height: 100%;
        margin-top: 1rem;
        display: flex;
        // flex-direction: column;
        // flex-direction: column;
        justify-content: center;
        @include for-desktop {
          margin-top: 0rem;
        }
        & .item {
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #29c4a9;
          color: white;
          padding: 10px;
          width: 45px;
          height: 45px;
          font-size: 1rem;
          border-radius: 50%;
          margin-right: 1rem;
          border: 0.4rem solid white;
          @include for-desktop {
            width: 60px;
            height: 60px;
            font-size: 1.4rem;
          }

          &.item--audio {
            background-color: #7F62F6;
          }
          &.item--video {
            background-color: #28A5FC;
          }
          &.item--network {
            background-color: #1ED78D;
          }

          &:hover {
            cursor: pointer;
          }

          &.item__action--turn-off {
            background-color: rgb(236, 69, 69);
          }
          &.item__action--call {
            background-color: #29c4a9;
          }
        }
      }
    }

    & .video__chat__join {
      margin-top: 1.5rem;
      display: flex;
      justify-content: center;
      & button {
        border-radius: 30px;
        font-size: 1.2rem;
      }
    }
  }
}
</style>
